import Guid from "_shared/guids/guid";
import Document from "documents/document";
import DocumentAccessSchema, {
  DocumentAccessJSON,
  DocumentAccessParams,
} from "permissions/resources/documents/_schemas/document-access-schema";
import DocumentContentsAccessSchema, {
  DocumentContentsAccessJSON,
  DocumentContentsAccessParams,
} from "permissions/resources/documents/_schemas/document-contents-access-schema";
import GeneralDocumentAccessSchema, {
  GeneralDocumentAccessJSON,
  GeneralDocumentAccessParams,
} from "permissions/resources/documents/_schemas/general-document-access-schema";
import { DocumentSubjectMatter } from "permissions/resources/documents/document-subject-matter";
import { Resource } from "permissions/resources/permission-resource";
import { 
  AccessLevel, 
  AnyResourceJSON, 
  ResourceFromJSON, 
  ResourceType, 
  Scope 
} from "permissions/resources/permission-resource-types";
import { registerResource } from "permissions/resources/resource-registry";

export class GeneralDocumentAccess extends Resource {
  public static create(params?: GeneralDocumentAccessParams): GeneralDocumentAccess {
    const parsed = GeneralDocumentAccessSchema.validate(params);
    return new GeneralDocumentAccess(parsed);
  }

  public static fromJSON<J extends AnyResourceJSON>(json: J): ResourceFromJSON<J> {
    const parsed = GeneralDocumentAccessSchema.deserialize(json as GeneralDocumentAccessJSON);
    return new GeneralDocumentAccess(parsed) as ResourceFromJSON<J>;
  }

  public static getPermutations(): GeneralDocumentAccess[] {
    const fieldIterations: [key: string, value: number[]][] = [
      ["accessLevel", Object.values(AccessLevel).filter((key) => !Number.isNaN(Number(key))).map(Number)],
      ["scope", Object.values(Scope).filter((key) => !Number.isNaN(Number(key))).map(Number)],
    ];
    return this.generatePermutations(GeneralDocumentAccess, fieldIterations) as GeneralDocumentAccess[];
  }

  public readonly resourceType = ResourceType.GeneralDocumentAccess;
  public readonly accessLevel?: AccessLevel;
  public readonly scope?: Scope;

  public get metaText(): string {
    return `documents ${this.accessLevel} ${this.scope}`;
  }

  public constructor(params?: GeneralDocumentAccessParams) {
    super(params);

    if (params) {
      this.accessLevel = params.accessLevel;
      this.scope = params.scope;
    }
  }

  public toJSON(): GeneralDocumentAccessJSON {
    return GeneralDocumentAccessSchema.serialize(this);
  }

  public override toString(): string {
    return `GeneralDocumentAccess {
      resourceType: ${this.resourceType}
      isDisabled: ${this.isDisabled ? "true" : "false"}
      resourceTypeText: ${this.resourceTypeText}
      accessLevel: ${this.accessLevel}
      scope: ${this.scope}
    }`;
  }
}

registerResource("GeneralDocumentAccess", (json) => GeneralDocumentAccess.fromJSON(json));

export class DocumentContentsAccess extends Resource {
  public static create(params?: DocumentContentsAccessParams): DocumentContentsAccess {
    const parsed = DocumentContentsAccessSchema.validate(params);
    return new DocumentContentsAccess(parsed);
  }

  public static fromJSON<J extends AnyResourceJSON>(json: J): ResourceFromJSON<J> {
    const parsed = DocumentContentsAccessSchema.deserialize(json as DocumentContentsAccessJSON);
    return new DocumentContentsAccess(parsed) as ResourceFromJSON<J>;
  }

  public static getPermutations(): DocumentContentsAccess[] {
    const fieldIterations: [key: string, value: number[]][] = [
      ["subjectMatter", Object.values(DocumentSubjectMatter).filter(value => !Number.isNaN(Number(value))).map(value => Number(value))],
      ["scope", Object.values(Scope).filter(value => !Number.isNaN(Number(value))).map(value => Number(value))],
    ];
    return this.generatePermutations(DocumentContentsAccess, fieldIterations) as DocumentContentsAccess[];
  }

  public readonly resourceType = ResourceType.DocumentContentsAccess;
  public readonly subjectMatter?: DocumentSubjectMatter;
  public readonly scope?: Scope;

  public get metaText(): string {
    return `document contents ${this.subjectMatter} ${this.scope}`;
  }

  public constructor(params?: DocumentContentsAccessParams) {
    super(params);

    if (params) {
      this.scope = params.scope;
      this.subjectMatter = params.subjectMatter;
    }
  }

  public toJSON(): DocumentContentsAccessJSON {
    return DocumentContentsAccessSchema.serialize(this);
  }

  public override toString(): string {
    return `DocumentContentsAccess {
      resourceType: ${this.resourceType}
      isDisabled: ${this.isDisabled ? "true" : "false"}
      resourceTypeText: ${this.resourceTypeText}
      subjectMatter: ${this.subjectMatter}
      scope: ${this.scope}
    }`;
  }
}

registerResource("DocumentContentsAccess", (json) => DocumentContentsAccess.fromJSON(json));

export class DocumentAccess extends Resource {
  public static create(params?: DocumentAccessParams): DocumentAccess {
    const parsed = DocumentAccessSchema.validate(params);
    return new DocumentAccess(parsed);
  }

  public static fromJSON<J extends AnyResourceJSON>(json: J): ResourceFromJSON<J> {
    const parsed = DocumentAccessSchema.deserialize(json as DocumentAccessJSON);
    return new DocumentAccess(parsed) as ResourceFromJSON<J>;
  }

  public readonly resourceType = ResourceType.DocumentAccess;
  public readonly documentId?: Guid;
  public readonly accessLevel?: AccessLevel;

  public constructor(params?: Omit<DocumentAccessParams, "resourceType">) {
    super(params);

    if (params) {
      this.accessLevel = params.accessLevel;
      this.documentId = params.documentId;
    }
  }

  public toJSON(): DocumentAccessJSON {
    return DocumentAccessSchema.serialize(this);
  }

  public override toString(options: {
    document: Document;
    isLoadingDocument: boolean | undefined;
    errorLoadingDocument: string | null | undefined;
  }): string {
    if (options?.isLoadingDocument) {
      return `Document: Loading...`;
    }

    if (options?.errorLoadingDocument) {
      return `Document: ${this.documentId?.value} (${this.accessLevel}) - Error: ${options.errorLoadingDocument}`;
    }

    return `Document: ${options?.document?.name || this.documentId?.value} (${this.accessLevel})`;
  }
}

registerResource("DocumentAccess", (json) => DocumentAccess.fromJSON(json));
